import { Badge, Button, colorsTuple, MantineColorsTuple, MantineThemeOverride, Paper, Tooltip } from '@mantine/core';

// Modern color palette with proper light/dark mode support
export const colorPalette = {
  // Primary brand color - Modern blue
  primary: [
    '#e7f5ff',
    '#d0ebff',
    '#a5d8ff',
    '#74c0fc',
    '#4dabf7',
    '#339af0',
    '#1E90FF',
    '#1c7ed6',
    '#1971c2',
    '#1864ab'
  ] as MantineColorsTuple,

  // Secondary color - Teal accent
  secondary: [
    '#e6fcf5',
    '#c3fae8',
    '#96f2d7',
    '#63e6be',
    '#38d9a9',
    '#20c997',
    '#12b886',
    '#0ca678',
    '#099268',
    '#087f5b'
  ] as MantineColorsTuple,

  // Success - Green
  success: [
    '#ebfbee',
    '#d3f9d8',
    '#b2f2bb',
    '#8ce99a',
    '#69db7c',
    '#51cf66',
    '#30DB5B',
    '#37b24d',
    '#2f9e44',
    '#2b8a3e'
  ] as MantineColorsTuple,

  // Available/Connected state - Light green
  available: colorsTuple('#8CE99A'),

  // Warning - Amber
  warning: [
    '#fff9db',
    '#fff3bf',
    '#ffec99',
    '#ffe066',
    '#ffd43b',
    '#fcc419',
    '#fab005',
    '#f59f00',
    '#f08c00',
    '#e67700'
  ] as MantineColorsTuple,

  // Error/Red - Custom error colors (Light: #EC0219, Dark: #FF3B30)
  error: [
    '#fff5f5',
    '#ffe3e3',
    '#ffc9c9',
    '#ffa8a8',
    '#ff8787',
    '#ff6b6b',
    '#EC0219',  // Light theme primary (index 6)
    '#FF3B30',  // Dark theme primary (index 7)
    '#d90216',
    '#c60214'
  ] as MantineColorsTuple,

  // Red - Alias for error color (used by Mantine components like Alert, Badge)
  red: [
    '#fff5f5',
    '#ffe3e3',
    '#ffc9c9',
    '#ffa8a8',
    '#ff8787',
    '#ff6b6b',
    '#EC0219',  // Light theme primary (index 6)
    '#FF3B30',  // Dark theme primary (index 7)
    '#d90216',
    '#c60214'
  ] as MantineColorsTuple,

  // Neutral grays for UI elements
  gray: [
    '#fafbfc',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#373737B2',
    '#495057',
    '#343a40',
    '#373737'
  ] as MantineColorsTuple,

  // Dark mode specific colors
  dark: [
    '#C9C9C9',
    '#b8b8b8',
    '#828282',
    '#696969',
    '#424242',
    '#3b3b3b',
    '#2e2e2e',
    '#242424',
    '#1f1f1f',
    '#141414'
  ] as MantineColorsTuple,

  // Primary header fill and buttons
  'primary-header-fill-and-buttons': [
    '#FAFAFA',
    '#F8F8F8',
    '#F6F6F6',
    '#F2F2F2',
    '#EEEEEE',
    '#E8E8E8',
    '#F4F4F4',  // Light mode primary (index 6)
    '#2E2E2E',  // Dark mode primary (index 7)
    '#282828',
    '#242424'
  ] as MantineColorsTuple,

};

// Theme configuration
export const themeConfig: MantineThemeOverride = {
  colors: colorPalette,
  primaryColor: 'primary',
  primaryShade: {light: 6, dark: 7},

  // Proper typography
  fontFamily: "Inter, sans-serif",
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

  // General Breakpoints
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '88em',
  },

  //Headings
  headings: {
    sizes: {
      h1: {
        fontSize: '1.25rem',   // 20px
      },
    }
  },

  // Modern font sizes
  fontSizes: {
    xs: '0.6125rem',   // 10px
    sm: '0.75rem',   // 12px
    md: '0.875rem',  // 14px
    lg: '1rem',      // 16px
    xl: '1.25rem',  // 20px
    xxl: '1.875rem',  // 30px
  },

  // Consistent spacing scale
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',    // 8px
    md: '0.6125rem',      // 10px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    xxl: '1.625rem',      // 26px
  },

  // Modern border radius
  radius: {
    xs: '0.125rem',  // 2px
    sm: '0.25rem',   // 4px
    md: '0.6125rem',    // 10px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
  },

  // Subtle shadows with card-specific moderate shadow
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    sm: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05)',
    md: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    lg: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    xl: '0 25px 50px rgba(0, 0, 0, 0.12), 0 12px 24px rgba(0, 0, 0, 0.08)',
    // Card-specific moderate shadow as requested
    card: '0 2px 4px rgba(0, 0, 0, 0.15)',
  },

  // Line heights
  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.4',
    lg: '1.55',
    xl: '1.65',
  },

  // Accessibility and focus settings
  white: '#ffffff',
  black: '#000000',
  focusRing: 'auto',
  fontSmoothing: true,
  autoContrast: true,        // Automatically ensure proper contrast ratios
  luminanceThreshold: 0.3,   // Threshold for WCAG compliance

  // Component specific styles
  components: {
    // Paper defaults - filled backgrounds and moderate shadows
    Paper: Paper.extend({
      styles: (theme: any) => ({
        root: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors.dark[7]})`,
          boxShadow: theme.shadows.card,
          padding: `0.8rem 1.5rem`
        },
      }),
    }),

    // Button defaults - md text size, md line height, and 400 font weight
    Button: Button.extend({
      styles: (theme: any) => ({
        root: {
          fontSize: theme.fontSizes.md,      // 0.875rem (14px)
          lineHeight: theme.lineHeights.md,  // 1.4
          fontWeight: 400,                   // Normal weight
        },
      }),
    }),

    // Badge defaults - medium font weight
    Badge: Badge.extend({
      defaultProps: {
        fw: 500,
      },
    }),

    // Modal component
    Modal: {
      styles: () => ({
        header: {
          // Make header transparent to inherit the background from the modal content
          backgroundColor: 'transparent',
        },
      }),
    },

    // App Shell components
    AppShell: {
      styles: (theme: any) => ({
        main: {
          backgroundColor: `light-dark(${theme.colors.gray[0]}, ${theme.colors.dark[8]})`,
        },
      }),
    },

    // Tooltip component - improved spacing and delay
    Tooltip: Tooltip.extend({
      defaultProps: {
        openDelay: 500, // 500ms delay before tooltip shows
      },
      styles: {
        tooltip: {
          padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)', // Add more breathing room
        },
      },
    }),

    // Notification component - add bottom margin for proper spacing and default border
    Notification: {
      defaultProps: {
        withBorder: true,
      },
      styles: () => ({
        root: {
          paddingTop: 'var(--mantine-spacing-md)',
          paddingBottom: 'var(--mantine-spacing-md)',
        },
      }),
    },
  },

  // Other theme overrides
  other: {
    // Custom semantic colors
    backgroundColors: {
      light: {
        primary: '#ffffff',
        secondary: '#f8f9fa',
        tertiary: '#e9ecef',
      },
      dark: {
        primary: '#1a1a1a',
        secondary: '#242424',
        tertiary: '#3b3b3b',
      },
    },

    // Semantic colors for different states
    semanticColors: {
      info: 'primary',
      success: 'success',
      warning: 'warning',
      error: 'error',
    },

    // Icon-specific colors
    iconColors: {
      refresh: 'success.6',  // Green refresh icon using success color
    },
  },
};

// CSS variables resolver for dynamic theme switching
export function cssVariablesResolver(theme: any) {
  return {
    variables: {
      // Border radius tokens
      '--mantine-radius-default': theme.radius.md,

      // Shadow tokens
      '--mantine-shadow-default': theme.shadows.sm,
      '--mantine-shadow-card': theme.shadows.card,

      // Semantic color variables using light-dark()
      '--mantine-color-surface': `light-dark(${theme.white}, ${theme.colors.dark[7]})`,
      '--mantine-color-surface-hover': `light-dark(${theme.colors.gray[1]}, ${theme.colors.dark[6]})`,
      '--mantine-color-border-default': `light-dark(${theme.colors.gray[3]}, ${theme.colors.dark[4]})`,

      // Icon colors
      '--mantine-color-refresh-icon': theme.colors.success[6],

      '--mantine-color-dimmed': '#373737B2'
    },
    light: {
      '--mantine-color-body': theme.colors.gray[0],
      '--mantine-color-text': theme.colors.gray[9],
      '--mantine-color-border': theme.colors.gray[3],
      '--mantine-color-hover': theme.colors.gray[1],
      '--mantine-color-navbar': theme.white,
      '--mantine-color-header': theme.colors.gray[0],
    },
    dark: {
      '--mantine-color-body': theme.colors.dark[8],
      '--mantine-color-text': theme.colors.dark[0],
      '--mantine-color-border': theme.colors.dark[6],
      '--mantine-color-hover': theme.colors.dark[5],
      '--mantine-color-navbar': theme.colors.dark[7],
      '--mantine-color-header': theme.colors.dark[8],
    },
  };
}